import { CustomSelectNewItem } from "@/components/extended/type";

export const ROLE_GATE = "GATE";
export const ROLE_DISTRIBUTION = "DISTRIBUTION";
export const ROLE_SUB = "SUB";
export const METER_HIERARCHY_ALL = "ALL";
export const METER_HIERARCHY_UNASSIGNED = "UNASSIGNED";

export const formatMeterHierarchy = (
  template: string,
  values: Record<string, string>,
): string =>
  Object.entries(values).reduce(
    (formattedText, [key, value]) =>
      formattedText.split(`{${key}}`).join(value),
    template,
  );

export const METER_HIERARCHY_ASSIGN_ROLE_OPTIONS: CustomSelectNewItem[] = [
  { label: "Gate", value: ROLE_GATE },
  { label: "Distribution", value: ROLE_DISTRIBUTION },
  { label: "Sub", value: ROLE_SUB },
];

export const METER_HIERARCHY_ROLE_FILTER_OPTIONS: CustomSelectNewItem[] = [
  { label: "All", value: METER_HIERARCHY_ALL },
  ...METER_HIERARCHY_ASSIGN_ROLE_OPTIONS,
  { label: "Unassigned", value: METER_HIERARCHY_UNASSIGNED },
];

export const ROOT_ROLES = [ROLE_GATE, ROLE_DISTRIBUTION];

export const TREE_INDENT_PX = 15;

export const TREE_ROW_HALF_HEIGHT_PX = 15;

export const FALLBACK_COLOR = "#2B64F4";
export const DEFAULT_TAG_COLOR = "#3498DB";

export const HEX_COLOR_REGEX =
  /^#(?:[\da-f]{3}|[\da-f]{4}|[\da-f]{6}|[\da-f]{8})$/i;

export const HIERARCHY_IMPORT_COLUMNS = {
  siteName: "SiteName",
  meterType: "MeterType",
  meterName: "MeterName",
  meterPropertyNumber: "MeterPropertyNumber",
  type: "Type",
  meterRole: "MeterRole",
  parentMeterName: "ParentMeterName",
  parentMeterNumber: "ParentMeterNumber",
  hierarchyLevel: "HierarchyLevel",
  level: "Level",
  apartmentOrTenancy: "ApartmentOrTenancy",
  currentTags: "CurrentTags",
  path: "Path",
  tagName: "TagName",
  shortDescription: "ShortDescription",
  order: "Order",
  currentColourHex: "CurrentColourHex",
  colourPreview: "Colour Preview",
  assignments: "Assignments",
} as const;

export const HIERARCHY_IMPORT_SHEET_NAMES = {
  hierarchy: "Hierarchy Import",
  tagAssignments: "Tag Assignments",
  tagDefinitions: "Tag Definitions",
} as const;

export const HIERARCHY_IMPORT_ISSUE_SOURCES = {
  workbook: "Workbook",
  meterInventory: "Meter inventory",
} as const;

export const HIERARCHY_IMPORT_ISSUE_CATEGORIES = {
  structure: "structure",
  meter: "meter",
  missing: "missing",
  parent: "parent",
  cycle: "cycle",
  role: "role",
  tag: "tag",
  data: "data",
} as const;

export const HIERARCHY_IMPORT_TYPES = {
  meter: "Meter",
  virtual: "Virtual",
} as const;

export const HIERARCHY_IMPORT_MAX_TAG_NAME_LENGTH = 255;

export const HIERARCHY_IMPORT_TYPE_MESSAGES = {
  meterNameRequired: "MeterName is required.",
  invalidType: "Type must be Meter or Virtual.",
  invalidTypeTitle: "Invalid meter type",
  invalidTypeDropdown: "Select Meter or Virtual from the dropdown.",
} as const;

export const HIERARCHY_IMPORT_METRICS = [
  { key: "physicalMatched", label: "Physical meters matched" },
  { key: "virtualToCreate", label: "Virtual meters to create" },
  { key: "parentRelationships", label: "Parent relationships" },
  { key: "roleChanges", label: "Role changes" },
  { key: "existingTagsReused", label: "Existing tags reused" },
  { key: "newTagsToCreate", label: "New tags to create" },
] as const;

export const HIERARCHY_IMPORT_TABS = {
  validation: "Validation",
  hierarchyPreview: "Hierarchy Preview",
  tags: "Tags",
} as const;

export const HIERARCHY_IMPORT_REQUIRED_HEADERS = {
  [HIERARCHY_IMPORT_SHEET_NAMES.hierarchy]: [
    HIERARCHY_IMPORT_COLUMNS.siteName,
    HIERARCHY_IMPORT_COLUMNS.meterType,
    HIERARCHY_IMPORT_COLUMNS.meterName,
    HIERARCHY_IMPORT_COLUMNS.meterPropertyNumber,
    HIERARCHY_IMPORT_COLUMNS.type,
    HIERARCHY_IMPORT_COLUMNS.meterRole,
    HIERARCHY_IMPORT_COLUMNS.parentMeterName,
    HIERARCHY_IMPORT_COLUMNS.parentMeterNumber,
  ],
  [HIERARCHY_IMPORT_SHEET_NAMES.tagAssignments]: [
    HIERARCHY_IMPORT_COLUMNS.meterName,
    HIERARCHY_IMPORT_COLUMNS.meterPropertyNumber,
    HIERARCHY_IMPORT_COLUMNS.tagName,
  ],
  [HIERARCHY_IMPORT_SHEET_NAMES.tagDefinitions]: [
    HIERARCHY_IMPORT_COLUMNS.tagName,
    HIERARCHY_IMPORT_COLUMNS.order,
  ],
} as const;

export const HIERARCHY_IMPORT_SHEETS = Object.values(
  HIERARCHY_IMPORT_SHEET_NAMES,
);

export const HIERARCHY_IMPORT_ROLES = [ROLE_GATE, ROLE_DISTRIBUTION, ROLE_SUB];

export const METER_HIERARCHY_REPORT_PAGE_WIDTH = 794;
export const METER_HIERARCHY_REPORT_PAGE_HEIGHT = 1123;

export const METER_HIERARCHY_EXPORT_MODES = {
  options: "options",
  pdf: "pdf",
  png: "png",
} as const;

export const METER_HIERARCHY_EXPORT_ALL_PAGES = "all" as const;

export const TAG_FILTER_MODES = {
  any: "ANY",
  all: METER_HIERARCHY_ALL,
} as const;

export const HIERARCHY_FILTER_VIEW_MODES = {
  tree: "TREE",
  path: "PATH",
  flat: "FLAT",
} as const;

export const SELECTION_SECTIONS = {
  tag: "tag",
  hierarchy: "hierarchy",
  meter: "meter",
} as const;

export const COLORFUL_CIRCLE_SIZES = {
  sm: "sm",
  md: "md",
} as const;

export const METER_SUMMARY_MODES = {
  assign: "assign",
  unassign: "unassign",
} as const;

export const HIERARCHY_SUMMARY_MODES = {
  setParent: "setParent",
  addPhysicalGate: "addPhysicalGate",
  addVirtualGate: "addVirtualGate",
  drop: "drop",
} as const;

export const HIERARCHY_SUMMARY_SECTION_KEYS = {
  added: "added",
  skipped: "skipped",
  errors: "errors",
} as const;

export const VIRTUAL_IDS_KEYS = {
  nodeIds: "nodeIds",
  hierarchyNodeIds: "hierarchyNodeIds",
} as const;

export const ROLE_COLORS: Record<string, string> = {
  gate: "#ee9d01",
  distribution: "#179c43",
  sub: "#2962ff",
};

export const METER_HIERARCHY_EXPORT_CSV_HEADERS = {
  siteName: "site_name",
  meterType: "meter_type",
  meterName: "meter_name",
  meterPropertyNumber: "meter_property_number",
  parentMeterName: "parent_meter_name",
  parentMeterPropertyNumber: "parent_meter_property_number",
  hierarchyLevel: "hierarchy_level",
  role: "role",
  isVirtual: "is_virtual",
  tags: "tags",
  path: "path",
};
